import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usuariosApi } from '../api/usuarios';
import { useIsAuthenticated } from './useAuth';
import type { 
  UsuariosFilters, 
  CriarUsuarioRequest, 
  AtualizarUsuarioRequest,
  AlterarStatusRequest,
  RedefinirSenhaRequest,
  AlterarSenhaRequest
} from '../types/usuarios';

// Keys para cache do React Query
export const usuariosKeys = {
  all: ['usuarios'] as const,
  lists: () => [...usuariosKeys.all, 'list'] as const,
  list: (filters: UsuariosFilters) => [...usuariosKeys.lists(), filters] as const,
  detail: (id: number) => [...usuariosKeys.all, 'detail', id] as const,
  stats: () => [...usuariosKeys.all, 'stats'] as const,
};

/**
 * Lista de usuários. `habilitado` existe porque a tela /usuarios também hospeda a
 * aba Papéis: quem tem só `gerenciar_papeis` entra na página mas leva 403 em
 * GET /api/usuarios (que é gateado por `gerenciar_usuarios`). Sem o gate aqui, o
 * 403 vira erro do React Query e derruba a página inteira — a pessoa não alcança
 * a aba que a permissão dela libera, e ainda re-dispara o 403 a cada 30s.
 */
export function useUsuarios(filters: UsuariosFilters = {}, habilitado = true) {
  const { isAuthenticated } = useIsAuthenticated();
  
  return useQuery({
    queryKey: usuariosKeys.list(filters),
    queryFn: () => usuariosApi.getUsuarios(filters),
    select: (response) => response.data,
    enabled: isAuthenticated && habilitado,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchInterval: 30000,
  });
}

// Hook para obter usuário específico
export function useUsuario(id: number) {
  const { isAuthenticated } = useIsAuthenticated();
  
  return useQuery({
    queryKey: usuariosKeys.detail(id),
    queryFn: () => usuariosApi.getUsuario(id),
    select: (response) => response.data.data,
    enabled: !!id && isAuthenticated, // Só executa se tiver ID e estiver autenticado
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

// Hook para estatísticas
/** Ver nota de `habilitado` em useUsuarios — mesmo gate, mesmo endpoint 403. */
export function useEstatisticasUsuarios(habilitado = true) {
  const { isAuthenticated } = useIsAuthenticated();
  
  return useQuery({
    queryKey: usuariosKeys.stats(),
    queryFn: () => usuariosApi.getEstatisticas(),
    select: (response) => response.data.data,
    enabled: isAuthenticated && habilitado,
    staleTime: 1000 * 60 * 2, // 2 minutos
    gcTime: 1000 * 60 * 5,
  });
}

// Hook para criar usuário
export function useCriarUsuario() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CriarUsuarioRequest) => usuariosApi.criarUsuario(data),
    onSuccess: () => {
      // Invalida cache de usuários e estatísticas
      queryClient.invalidateQueries({ queryKey: usuariosKeys.lists() });
      queryClient.invalidateQueries({ queryKey: usuariosKeys.stats() });
    },
  });
}

// Hook para atualizar usuário
export function useAtualizarUsuario() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarUsuarioRequest }) =>
      usuariosApi.atualizarUsuario(id, data),
    onSuccess: (_, variables) => {
      // Invalida cache específico e listas
      queryClient.invalidateQueries({ queryKey: usuariosKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: usuariosKeys.lists() });
      queryClient.invalidateQueries({ queryKey: usuariosKeys.stats() });
    },
  });
}

// Hook para alterar status
export function useAlterarStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AlterarStatusRequest }) =>
      usuariosApi.alterarStatus(id, data),
    onSuccess: (_, variables) => {
      // Invalida cache específico e listas
      queryClient.invalidateQueries({ queryKey: usuariosKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: usuariosKeys.lists() });
      queryClient.invalidateQueries({ queryKey: usuariosKeys.stats() });
    },
  });
}

// Hook para redefinir senha
export function useRedefinirSenha() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: RedefinirSenhaRequest }) =>
      usuariosApi.redefinirSenha(id, data),
    onSuccess: (_, variables) => {
      // Apenas invalida o cache do usuário específico
      queryClient.invalidateQueries({ queryKey: usuariosKeys.detail(variables.id) });
    },
  });
}

// Hook para alterar própria senha
export function useAlterarSenha() {
  return useMutation({
    mutationFn: (data: AlterarSenhaRequest) => usuariosApi.alterarSenha(data),
  });
}

// Hook para alterar senha de usuário manualmente (admin)
export function useAlterarSenhaManual() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: RedefinirSenhaRequest }) =>
      usuariosApi.alterarSenhaManual(id, data),
    onSuccess: (_, variables) => {
      // Apenas invalida o cache do usuário específico
      queryClient.invalidateQueries({ queryKey: usuariosKeys.detail(variables.id) });
    },
  });
}

// Hook para invalidar cache de usuários
export function useInvalidateUsuarios() {
  const queryClient = useQueryClient();
  
  return () => {
    queryClient.invalidateQueries({ queryKey: usuariosKeys.all });
  };
}